/*
 * Back to the same place after the language / currency / units popup reloads the
 * page. rememberPlace() runs just before the save (the popup's Save button calls
 * window.bdRememberPlace): it keeps the scroll and what the page itself adds —
 * `bd:remember-place` hands listeners an object to fill (the catalog map puts its
 * open state, chosen listing and position there) — for this address. On the reload,
 * if the address is the same one in any language, the scroll comes back and
 * `bd:restore-place` tells the page the rest. A filter or the sort leaving for a
 * new address calls it with that address, so the open catalog map comes along.
 */
const KEY = 'bd:place';
const FRESH_MS = 60000;

// "/ru/miami" and "/miami" are the same place in two languages.
function placePath(path) {
    return path.replace(/^\/(ru|es)(?=\/|$)/, '') || '/';
}

/** What was remembered for this address, if it is fresh. */
function remembered() {
    try {
        const place = JSON.parse(window.sessionStorage.getItem(KEY) || 'null');

        return place && Date.now() - place.at <= FRESH_MS && place.path === placePath(window.location.pathname) ? place : null;
    } catch (e) {
        return null;
    }
}

/**
 * `target` is given when the visitor leaves for another selection rather than
 * reloading this one (a filter or the sort, see PopupFilter and level.blade.php):
 * the place is kept for THAT address. Only the open map comes along — the listing
 * in its preview may not be among the new results, the scroll belongs to the old
 * list, and the map's position only while the path (the city, the type) stays.
 */
export function rememberPlace(target) {
    const extras = {};
    document.dispatchEvent(new CustomEvent('bd:remember-place', { detail: extras }));

    const here = placePath(window.location.pathname);
    let path = here;
    let y = window.scrollY;

    if (typeof target === 'string') {
        if (!extras.phoneMap) {
            return;
        }

        path = placePath(new URL(target, window.location.href).pathname);
        y = 0;
        delete extras.preview;

        if (path !== here) {
            delete extras.mapHash;
        }
    }

    try {
        window.sessionStorage.setItem(KEY, JSON.stringify(Object.assign({}, extras, {
            path,
            y,
            at: Date.now(),
        })));
    } catch (e) {
        // Private mode: the reload simply starts at the top.
    }
}

/**
 * The map's position goes back into the address before anything mounts: CatalogMap
 * reads it from there (readHash).
 */
export function restorePlaceHash() {
    const place = remembered();

    if (place && place.mapHash) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search + place.mapHash);
    }
}

/** The rest, once the page has loaded: the scroll, and `bd:restore-place` for the page. */
export function restorePlaceOnLoad() {
    const restore = () => {
        const place = remembered();

        try {
            window.sessionStorage.removeItem(KEY);
        } catch (e) {
            return;
        }

        if (!place) {
            return;
        }

        window.scrollTo(0, place.y || 0);
        document.dispatchEvent(new CustomEvent('bd:restore-place', { detail: place }));
    };

    if (document.readyState === 'complete') {
        restore();
    } else {
        window.addEventListener('load', restore);
    }
}
